import { ENCRYPTED_CREDENTIALS, EncryptionService } from './encryption';

export interface TwilioResponse {
  success: boolean;
  data?: any;
  error?: string;
  status?: number;
}

export class TwilioService {
  private static getCredentials() {
    return {
      sid: EncryptionService.decrypt(ENCRYPTED_CREDENTIALS.SID),
      token: EncryptionService.decrypt(ENCRYPTED_CREDENTIALS.TOKEN)
    };
  }

  static async checkNumber(phoneNumber: string): Promise<TwilioResponse> {
    const { sid, token } = this.getCredentials();
    const encodedPhone = encodeURIComponent(phoneNumber);
    const url = `https://accounts.twilio.com/v1/SafeList/Numbers?PhoneNumber=${encodedPhone}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': 'Basic ' + btoa(`${sid}:${token}`)
        }
      });

      if (response.status === 200) {
        const data = await response.json();
        return { success: true, data };
      } else if (response.status === 404) {
        return { success: false, error: 'Number is not in the Safe List.' };
      } else if (response.status === 401) {
        return { success: false, error: 'Authentication Error: Please check the Twilio credentials in the configuration.' };
      } else {
        const errorText = await response.text();
        return { success: false, error: `Error (${response.status}): ${errorText}`, status: response.status };
      }
    } catch (error) {
      return { success: false, error: `Connection Error: ${error instanceof Error ? error.message : error}` };
    }
  }

  static async addNumber(phoneNumber: string): Promise<TwilioResponse> {
    const { sid, token } = this.getCredentials();
    const formData = new URLSearchParams();
    formData.append('PhoneNumber', phoneNumber);

    try {
      const response = await fetch('https://accounts.twilio.com/v1/SafeList/Numbers', {
        method: 'POST',
        headers: {
          'Authorization': 'Basic ' + btoa(`${sid}:${token}`),
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData
      });

          if (response.status === 200 || response.status === 201) {
            const data = await response.json();
            return { success: true, data };
          } else if (response.status === 401) {
            return { success: false, error: 'Authentication Error: Please check the Twilio credentials in the configuration.' };
          } else {
            const errorText = await response.text();
            return { success: false, error: `Error (${response.status}): ${errorText}`, status: response.status };
          }
        } catch (error) {
          return { success: false, error: `Connection Error: ${error instanceof Error ? error.message : error}` };
        }
  }

  static async removeNumber(phoneNumber: string): Promise<TwilioResponse> {
    const { sid, token } = this.getCredentials();
    const encodedPhone = encodeURIComponent(phoneNumber);
    const url = `https://accounts.twilio.com/v1/SafeList/Numbers?PhoneNumber=${encodedPhone}`;

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Basic ' + btoa(`${sid}:${token}`)
        }
      });

      if (response.status === 204) {
        return { success: true, data: 'Number successfully removed.' };
      } else if (response.status === 401) {
        return { success: false, error: 'Authentication Error: Please check the Twilio credentials in the configuration.' };
      } else {
        const errorText = await response.text();
        return { success: false, error: `Error removing number (${response.status}): ${errorText}`, status: response.status };
      }
    } catch (error) {
      return { success: false, error: `Connection Error: ${error instanceof Error ? error.message : error}` };
    }
  }
}
